import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PatientService } from './patient.service';
import { IntegrationType } from '../enums/integration-type.enum';
import { RequestService } from './request.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from '../modules/shared/components/dialog/dialog.component';
import { AmplitudeService } from './amplitude.service';
import { AmplitudeEvents } from '../enums/amplitude-events';
import { Patient } from '../models/patient.model';
import { CallService } from './call.service';
import { Router } from '@angular/router';
import { AppPaths } from '../enums/app-paths.enum';

interface PatientValidationState {
  isLoading: boolean;
  isValid: boolean | null;
  patientId: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class WebServiceStateHandler {
  private patientValidationState = new BehaviorSubject<PatientValidationState>({
    isLoading: false,
    isValid: null,
    patientId: null
  });

  public validationState$ = this.patientValidationState.asObservable();

  constructor(
    private patientService: PatientService,
    private requestService: RequestService,
    private matDialog: MatDialog,
    private amplitudeService: AmplitudeService,
    private callService: CallService,
    private router: Router
  ) {}

  async startPatientActiveValidation(patient?: Patient): Promise<void> {
    const patientToValidate = patient || (await this.patientService.getMe());
    const currentState = this.patientValidationState.value;

    // If already validating or we have a result, don't validate again
    if (currentState.patientId === patientToValidate.id && (currentState.isLoading || currentState.isValid !== null)) {
      return;
    }

    this.patientValidationState.next({
      isLoading: true,
      isValid: null,
      patientId: patientToValidate.id
    });

    this.checkPatientInWebService(patientToValidate)
      .then(() => {
        // Patient is active in ws
        this.setPatientValidationResult(patientToValidate.id, true);
      })
      .catch(async (error) => {
        const allowProgress = error?.status === 500;
        this.setPatientValidationResult(patientToValidate.id, allowProgress);

        if (allowProgress) return;

        this.amplitudeService.showEvent(AmplitudeEvents.PATIENT_DISABLED_WEB_SERVICE);
        if (this.isAffiliateMemberNotActiveError(error)) await this.handleAffiliateMemberNotActive(patientToValidate, error?.error?.message);

        // At last display pop up in Auth interceptor and logout patient
      });
  }

  async isPatientActive(patient?: Patient): Promise<boolean | null> {
    const patientToValidate = patient || (await this.patientService.getMe());
    const currentState = this.patientValidationState.value;

    // If already have a result, return it immediately
    if (currentState.patientId === patientToValidate.id && currentState.isValid !== null) {
      return currentState.isValid;
    }

    // Ensure validation has started
    if (currentState.patientId !== patientToValidate.id || !currentState.isLoading) {
      this.startPatientActiveValidation(patientToValidate);
    }

    this.showWaitingDialog();

    return new Promise((resolve) => {
      const subscription = this.validationState$.subscribe((validationState) => {
        if (validationState.patientId === patientToValidate.id && !validationState.isLoading) {
          this.matDialog.closeAll();
          subscription.unsubscribe();
          resolve(validationState.isValid);
        }
      });
    });
  }

  getCurrentValidationState(): PatientValidationState {
    return this.patientValidationState.value;
  }

  showWaitingDialog(): void {
    this.matDialog.open(DialogComponent, {
      width: '80%',
      maxWidth: '350px',
      data: {
        title: 'dialog_processing_request_title',
        message: 'dialog_processing_request_message'
      },
      disableClose: true
    });
  }

  private showPatientNotActiveDialog(message: string): void {
    this.matDialog.open(DialogComponent, {
      width: '80%',
      maxWidth: '350px',
      data: {
        title: 'dialog_title_warning',
        message,
        actions: [{ action: () => this.redirectToHome() }]
      }
    });
  }

  private async handleAffiliateMemberNotActive(patient: Patient, message: string): Promise<void> {
    await this.patientService.removeFamilyGroupMember(patient.id);
    this.showPatientNotActiveDialog(message);
  }

  private redirectToHome(): void {
    this.callService.endCallFlow();
    this.router.navigate([AppPaths.NAVBAR, AppPaths.HOME]);
  }

  private setPatientValidationResult(patientId: string, isValid: boolean): void {
    this.patientValidationState.next({
      isLoading: false,
      isValid,
      patientId
    });
  }

  private isAffiliateMemberNotActiveError(error: any): boolean {
    return error?.error?.type === 'affiliateMemberNotActive';
  }

  resetPatientValidation(): void {
    this.patientValidationState.next({
      isLoading: false,
      isValid: null,
      patientId: null
    });
  }

  async checkPatientInWebService(patient?: Patient): Promise<boolean> {
    patient = patient || (await this.patientService.getMe());
    const { EXCEL_AFFILIATE_SERVICE, PROVIDER_TOKEN_SERVICE } = IntegrationType;
    if (patient.provider.serviceObjectName === EXCEL_AFFILIATE_SERVICE || patient.provider.serviceObjectName === PROVIDER_TOKEN_SERVICE) {
      return true;
    }

    return this.requestService.get(`/affiliate/${patient.id}/checkService`);
  }
}
